/**
 * Resolve a catalog vitola from a cigar line name.
 *
 * Cigar lines are often named after a size (e.g. "Churchill") or include a
 * size token (e.g. "Black Friday 2024 Robusto Extra"). Prefer exact matches,
 * then the longest vitola name that appears as a whole-word token.
 */

export type VitolaLike = {
  id: string
  name: string
  length?: number | string | null
  ringGauge?: number | string | null
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function hasDimensions(vitola: VitolaLike): boolean {
  const length = Number(vitola.length)
  const ring = Number(vitola.ringGauge)
  return Number.isFinite(length) && length > 0 && Number.isFinite(ring) && ring > 0
}

export function resolveVitolaFromLineName<T extends VitolaLike>(
  lineName: string | null | undefined,
  vitolas: T[] | null | undefined
): T | undefined {
  if (!lineName?.trim() || !Array.isArray(vitolas) || vitolas.length === 0) {
    return undefined
  }

  const normalized = lineName.trim().toLowerCase()

  const exact = vitolas.find(
    (v) => v.name.trim().toLowerCase() === normalized && hasDimensions(v)
  )
  if (exact) return exact

  // Longest name first so "Double Corona" wins over "Corona"
  const candidates = [...vitolas]
    .filter((v) => v.name.trim().length >= 4 && hasDimensions(v))
    .sort((a, b) => b.name.trim().length - a.name.trim().length)

  for (const vitola of candidates) {
    const vitolaName = vitola.name.trim().toLowerCase()
    if (normalized.endsWith(` ${vitolaName}`)) {
      return vitola
    }
  }

  for (const vitola of candidates) {
    const vitolaName = vitola.name.trim().toLowerCase()
    const pattern = new RegExp(`(?:^|\\s)${escapeRegExp(vitolaName)}(?:\\s|$)`, 'i')
    if (pattern.test(normalized)) {
      return vitola
    }
  }

  // Line-scoped catalogs often have a single size; select it when unambiguous.
  const dimensional = vitolas.filter(hasDimensions)
  if (dimensional.length === 1) {
    return dimensional[0]
  }

  return undefined
}
